import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import DetailsSectionRenderer from "../../../../components/DetailsSectionRenderer";
import { detailsAddressFields } from "../../../../constants/forms/addressFields";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import { maskCep } from "../../../../utils/maskUtils";
import useForm from "../../../../hooks/useForm";
import { setDefaultFieldValues } from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const SupplierAddressDetailsPage = () => {
  const navigate = useNavigate();
  const { id, addressId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { getByColumn: fetchById } = useBaseService(navigate);

  const { formData, setFormData, formatData } = useForm(
    setDefaultFieldValues(detailsAddressFields),
  );

  const {
    data: addressData,
    isLoading: isLoadingAddress,
    isError: isErrorAddress,
    error: addressError,
  } = useQueryWithLoader({
    queryKey: ["equipment_supplierAddress", id, addressId],
    queryFn: async () => {
      showLoader();
      const response = await fetchById(
        entities.suppliers.addresses.getById(id, addressId),
      );
      return response.result;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!isLoadingAddress && !isErrorAddress && !addressError && addressData) {
      formatData(addressData, detailsAddressFields);
      setFormData((prev) => ({
        ...prev,
        zip: maskCep(addressData.zip || ""),
      }));
      hideLoader();
    }
    if (isErrorAddress || addressError) {
      showNotification(
        "error",
        `Erro ao carregar detalhes do endereço: ${
          addressError.response?.data?.message || addressError.message
        }`,
      );
      hideLoader();
      navigate(`/aluguel/fornecedores/detalhes/${id}`, {
        replace: true,
        state: {
          type: "error",
          message: "Endereço não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [isLoadingAddress, isErrorAddress, addressError, addressData]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Endereço do Fornecedor"
        showBackButton={true}
        backUrl={`/aluguel/fornecedores/detalhes/${id}`}
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          sections={detailsAddressFields}
          formData={formData}
          isLoading={isLoadingAddress}
          readOnly={true}
        />
      </div>
    </MainLayout>
  );
};

export default SupplierAddressDetailsPage;
